import { Component } from '@theme/component';
import { clamp, prefersReducedMotion, viewTransition, scheduler } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Vertical Scroll Slider - Clean implementation with stacking effect
 * Features:
 * - Stacking effect where slides appear to stack on top of each other
 * - Normal scrolling past first/last slides to access footer and content above
 * - Smooth transitions with proper z-index management
 */
export class VerticalScrollSlider extends Component {
  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      if (!this.isConnected) return;
    }

    // Set slide count for proper spacing
    const { slides } = this;
    if (slides && slides.length > 0) {
      this.style.setProperty('--slide-count', slides.length.toString());
    }

    // Add event listeners
    this.addEventListener('wheel', this.#handleWheel, { passive: false });
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: false });
    document.addEventListener('keydown', this.#handleKeydown);

    // Initialize
    this.current = this.dataset.initialSlide ? parseInt(this.dataset.initialSlide, 10) : 0;
    
    scheduler.schedule(() => {
      // Set initial slide without animation
      const { slides } = this;
      if (slides && slides.length > 0) {
        // Disable transitions temporarily
        slides.forEach(slide => {
          if (slide instanceof HTMLElement) {
            slide.style.transition = 'none';
          }
          slide.setAttribute('aria-hidden', 'true');
        });
        
        // Set the initial slide
        const initialSlide = slides[this.current];
        if (initialSlide) {
          initialSlide.setAttribute('aria-hidden', 'false');
        }
        
        // Re-enable transitions
        setTimeout(() => {
          slides.forEach(slide => {
            if (slide instanceof HTMLElement) {
              slide.style.transition = '';
            }
          });
        }, 100);
      }
      
      // Setup resize observer
      this.#resizeObserver = new ResizeObserver(() => {
        if (slides && slides.length > 0) {
          this.style.setProperty('--slide-count', slides.length.toString());
        }
      });
      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('wheel', this.#handleWheel);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchmove', this.#handleTouchMove);
    this.removeEventListener('touchend', this.#handleTouchEnd);
    document.removeEventListener('keydown', this.#handleKeydown);
    
    if (this.#wheelTimeout) {
      clearTimeout(this.#wheelTimeout);
    }
    
    this.#resizeObserver?.disconnect();
  }

  /**
   * Handle wheel events for slide navigation
   */
  #handleWheel = (event) => {
    const { slides } = this;
    if (!slides || slides.length <= 1) return;

    const deltaY = event.deltaY;
    if (Math.abs(deltaY) < 50) return;
    
    const currentIndex = this.current;
    const isLastSlide = currentIndex === slides.length - 1;
    const isFirstSlide = currentIndex === 0;
    
    // Allow normal scrolling past boundaries
    if ((deltaY > 0 && isLastSlide) || (deltaY < 0 && isFirstSlide)) {
      return;
    }
    
    event.preventDefault();
    
    if (this.#wheelTimeout) {
      clearTimeout(this.#wheelTimeout);
    }
    
    this.#wheelTimeout = setTimeout(() => {
      if (deltaY > 0 && currentIndex < slides.length - 1) {
        this.select(currentIndex + 1, event);
      } else if (deltaY < 0 && currentIndex > 0) {
        this.select(currentIndex - 1, event);
      }
      this.#wheelTimeout = null;
    }, 100);
  };

  /**
   * Handle touch events for mobile
   */
  #handleTouchStart = (event) => {
    if (event.touches && event.touches[0]) {
      this.#touchStartY = event.touches[0].clientY;
      this.#touchStartTime = Date.now();
    }
  };

  #handleTouchMove = (event) => {
    if (!this.#touchStartY || !event.touches || !event.touches[0]) return;
    
    const { slides } = this;
    if (!slides || slides.length <= 1) return;
    
    const currentIndex = this.current;
    const isLastSlide = currentIndex === slides.length - 1;
    const isFirstSlide = currentIndex === 0;
    
    // Allow normal scrolling at boundaries
    if (isLastSlide || isFirstSlide) {
      return;
    }
    
    event.preventDefault();
  };

  #handleTouchEnd = (event) => {
    if (!this.#touchStartY || !this.#touchStartTime || !event.changedTouches || !event.changedTouches[0]) return;
    
    const { slides } = this;
    if (!slides || slides.length <= 1) return;

    const currentY = event.changedTouches[0].clientY;
    const deltaY = this.#touchStartY - currentY;
    const deltaTime = Date.now() - this.#touchStartTime;
    
    const minSwipeDistance = 50;
    const maxSwipeTime = 500;
    
    if (Math.abs(deltaY) > minSwipeDistance && deltaTime < maxSwipeTime) {
      const currentIndex = this.current;
      const isLastSlide = currentIndex === slides.length - 1;
      const isFirstSlide = currentIndex === 0;
      
      if (deltaY > 0 && currentIndex < slides.length - 1 && !isLastSlide) {
        this.select(currentIndex + 1, event);
      } else if (deltaY < 0 && currentIndex > 0 && !isFirstSlide) {
        this.select(currentIndex - 1, event);
      }
    }
    
    this.#touchStartY = null;
    this.#touchStartTime = null;
  };

  /**
   * Handle keyboard navigation
   */
  #handleKeydown = (event) => {
    const { slides } = this;
    if (!slides || slides.length <= 1) return;

    const currentIndex = this.current;
    
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        if (currentIndex > 0) {
          this.select(currentIndex - 1, event);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (currentIndex < slides.length - 1) {
          this.select(currentIndex + 1, event);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  };

  /**
   * Select a slide with stacking animation
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    // Parse input
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const slides = this.refs.slides;
        if (Array.isArray(slides)) {
          const requestedSlide = slides.find((slide) => slide.getAttribute('slide-id') == input.id);
          if (!requestedSlide || !this.slides) return;
          return this.slides.indexOf(requestedSlide);
        }
      }
    })();

    if (requestedIndex === undefined || isNaN(requestedIndex)) return;

    const { slides } = this;
    if (!slides?.length) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Handle infinite loop
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;
    const currentSlide = slides[previousIndex];
    const isScrollingUp = index < previousIndex;
    const isScrollingDown = index > previousIndex;

    // Create stacking animation
    if (animate && currentSlide && previousIndex !== index) {
      if (isScrollingDown) {
        this.#animateScrollDown(currentSlide, slide);
      } else if (isScrollingUp) {
        this.#animateScrollUp(currentSlide, slide);
      }
    } else {
      this.#instantTransition(slides, slide);
    }
    
    // Update current index
    this.current = index;
    
    // Scroll page to appropriate position
    const slideHeight = window.innerHeight;
    const targetScrollPosition = index * slideHeight;
    
    if (animate) {
      window.scrollTo({
        top: targetScrollPosition,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo({
        top: targetScrollPosition,
        behavior: 'instant'
      });
    }

    // Dispatch event
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Animate scroll down (new slide comes from below)
   */
  #animateScrollDown(currentSlide, newSlide) {
    if (currentSlide instanceof HTMLElement) {
      currentSlide.style.zIndex = '5';
    }
    if (newSlide instanceof HTMLElement) {
      newSlide.style.zIndex = '15';
    }
    newSlide.setAttribute('aria-hidden', 'false');
    
    setTimeout(() => {
      currentSlide.setAttribute('aria-hidden', 'true');
      if (currentSlide instanceof HTMLElement) {
        currentSlide.style.zIndex = '';
      }
      if (newSlide instanceof HTMLElement) {
        newSlide.style.zIndex = '';
      }
    }, 600);
  }

  /**
   * Animate scroll up (current slide moves down)
   */
  #animateScrollUp(currentSlide, previousSlide) {
    if (previousSlide instanceof HTMLElement) {
      previousSlide.style.transition = 'none';
      previousSlide.style.zIndex = '10';
    }
    previousSlide.setAttribute('aria-hidden', 'false');
    
    if (previousSlide instanceof HTMLElement) {
      previousSlide.offsetHeight; // Force reflow
    }
    
    if (currentSlide instanceof HTMLElement) {
      currentSlide.style.zIndex = '15';
      currentSlide.style.transform = 'translateY(100vh)';
    }
    
    setTimeout(() => {
      currentSlide.setAttribute('aria-hidden', 'true');
      if (currentSlide instanceof HTMLElement) {
        currentSlide.style.zIndex = '';
        currentSlide.style.transform = '';
      }
      if (previousSlide instanceof HTMLElement) {
        previousSlide.style.zIndex = '';
        previousSlide.style.transition = '';
      }
    }, 600);
  }

  /**
   * Instant transition without animation
   */
  #instantTransition(slides, targetSlide) {
    slides.forEach(s => {
      s.setAttribute('aria-hidden', 'true');
      if (s instanceof HTMLElement) {
        s.style.zIndex = '';
        s.style.transform = '';
        s.style.transition = '';
      }
    });
    targetSlide.setAttribute('aria-hidden', 'false');
  }

  /**
   * Get slides array
   */
  get slides() {
    const slides = this.refs.slides;
    return Array.isArray(slides) ? slides : [];
  }

  /**
   * Get infinite setting
   */
  get infinite() {
    return this.hasAttribute('infinite');
  }

  /**
   * Get disabled state
   */
  get disabled() {
    return this.hasAttribute('disabled');
  }

  /**
   * Set disabled state
   */
  set disabled(value) {
    if (value) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  /**
   * Current slide index
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  set current(value) {
    const { current, thumbnails, dots } = this.refs;

    this.#current = value;

    if (current && current instanceof HTMLElement) {
      current.textContent = `${value + 1}`;
    }

    for (const controls of [thumbnails, dots]) {
      if (Array.isArray(controls)) {
        controls.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
      }
    }
  }

  /**
   * Disabled state for transitions
   */
  #disabled = false;

  /**
   * Resize observer
   */
  #resizeObserver = null;

  /**
   * Touch tracking
   */
  #touchStartY = null;
  #touchStartTime = null;

  /**
   * Wheel event debouncing
   */
  #wheelTimeout = null;

  /**
   * Close the slider
   */
  close() {
    this.remove();
  }
}

if (!customElements.get('vertical-scroll-slider')) {
  customElements.define('vertical-scroll-slider', VerticalScrollSlider);
} 